import React, { useEffect, useState } from 'react';
import { masterSettingsAPI } from '@/api/api';
import { Plus, Edit, Trash2, Search, Award, Star, TrendingUp, Users, BookOpen, Calendar } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';

const Scholarships = () => {
  const [scholarships, setScholarships] = useState([]);
  const [courses, setCourses] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCourseFilter, setSelectedCourseFilter] = useState('');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentScholarship, setCurrentScholarship] = useState(null);
  const [entryTypeFilter, setEntryTypeFilter] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    criteria: '',
    amount: '',
    course_id: '',
    course_name: '',
    session_id: '',
    session_name: '',
    is_lateral_entry: false,
    entry_type: 'Regular'
  });

  useEffect(() => {
    fetchScholarships();
    fetchCourses();
  }, []);

  // Fetch sessions when course changes
  useEffect(() => {
    if (formData.course_id) {
      fetchSessionsForCourse(formData.course_id);
    } else {
      setSessions([]);
    }
  }, [formData.course_id]);

  const fetchCourses = async () => {
    try {
      const response = await masterSettingsAPI.getCourses();
      setCourses(response.data || []);
    } catch (error) {
      console.error('Error fetching courses:', error);
    }
  };

  const fetchSessionsForCourse = async (courseId) => {
    try {
      const response = await masterSettingsAPI.getSessions();
      setSessions(response.data || []);
    } catch (error) {
      console.error('Error fetching sessions:', error);
    }
  };

  const fetchScholarships = async () => {
    try {
      const response = await masterSettingsAPI.getScholarships();
      setScholarships(response.data);
    } catch (error) {
      console.error('Error fetching scholarships:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.course_id) {
      alert('Please select a course');
      return;
    }
    if (!formData.session_id) {
      alert('Please select a session');
      return;
    }
    
    try {
      const selectedCourse = courses.find(c => c.id === formData.course_id);
      const selectedSession = sessions.find(s => s.id === formData.session_id);
      
      const submitData = {
        ...formData,
        amount: parseFloat(formData.amount),
        course_name: selectedCourse?.name || '',
        session_name: selectedSession?.name || ''
      };
      
      if (editMode) {
        await masterSettingsAPI.updateScholarship(currentScholarship.id, submitData);
      } else {
        await masterSettingsAPI.createScholarship(submitData);
      }
      setIsDialogOpen(false);
      setEditMode(false);
      setCurrentScholarship(null);
      setFormData({ name: '', criteria: '', amount: '', course_id: '', course_name: '', session_id: '', session_name: '', is_lateral_entry: false, entry_type: 'Regular' });
      fetchScholarships();
    } catch (error) {
      console.error('Error saving scholarship:', error);
      alert('Failed to save scholarship');
    }
  };

  const handleEdit = (scholarship) => {
    setCurrentScholarship(scholarship);
    setFormData({
      name: scholarship.name,
      criteria: scholarship.criteria,
      amount: scholarship.amount.toString(),
      course_id: scholarship.course_id || '',
      course_name: scholarship.course_name || '',
      session_id: scholarship.session_id || '',
      session_name: scholarship.session_name || '',
      is_lateral_entry: scholarship.is_lateral_entry || false,
      entry_type: scholarship.entry_type || (scholarship.is_lateral_entry ? 'Lateral' : 'Regular')
    });
    // Fetch sessions for the course if editing
    if (scholarship.course_id) {
      fetchSessionsForCourse(scholarship.course_id);
    }
    setEditMode(true);
    setIsDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this scholarship?')) {
      try {
        await masterSettingsAPI.deleteScholarship(id);
        fetchScholarships();
      } catch (error) {
        console.error('Error deleting scholarship:', error);
        alert('Failed to delete scholarship');
      }
    }
  };

  const filteredScholarships = scholarships.filter(scholarship => {
    const matchesSearch = scholarship.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      scholarship.criteria.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCourse = selectedCourseFilter === '' || scholarship.course_id === selectedCourseFilter;
    const entryType = scholarship.entry_type || (scholarship.is_lateral_entry ? 'Lateral' : 'Regular');
    const matchesEntry = entryTypeFilter === '' || entryType === entryTypeFilter;
    return matchesSearch && matchesCourse && matchesEntry;
  });

  // Group by course
  const groupedByCourse = filteredScholarships.reduce((acc, scholarship) => {
    const courseKey = scholarship.course_name || 'Uncategorized';
    if (!acc[courseKey]) {
      acc[courseKey] = [];
    }
    acc[courseKey].push(scholarship);
    return acc;
  }, {});

  // Sort by amount (highest first)
  const sortedScholarships = [...filteredScholarships].sort((a, b) => b.amount - a.amount);

  // Calculate statistics
  const totalScholarships = scholarships.length;
  const totalAmount = scholarships.reduce((sum, s) => sum + s.amount, 0);
  const avgAmount = totalScholarships > 0 ? totalAmount / totalScholarships : 0;
  const activeScholarships = scholarships.filter(s => s.is_active).length;
  const maxScholarship = scholarships.length > 0 ? Math.max(...scholarships.map(s => s.amount)) : 0;
  const minScholarship = scholarships.length > 0 ? Math.min(...scholarships.map(s => s.amount)) : 0;

  if (loading) {
    return <div className="flex items-center justify-center h-64" data-testid="loading">Loading...</div>;
  }

  return (
    <div className="space-y-6" data-testid="scholarships-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-800">Scholarships Management</h1>
          <p className="text-gray-600 mt-1">Manage course-wise scholarship programs and awards</p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={(open) => {
          setIsDialogOpen(open);
          if (!open) {
            setEditMode(false);
            setCurrentScholarship(null);
            setFormData({ name: '', criteria: '', amount: '', course_id: '', course_name: '', session_id: '', session_name: '', is_lateral_entry: false, entry_type: 'Regular' });
            setSessions([]);
          }
        }}>
          <DialogTrigger asChild>
            <Button className="bg-blue-600 hover:bg-blue-700" data-testid="add-scholarship-button">
              <Plus size={20} className="mr-2" /> Add Scholarship
            </Button>
          </DialogTrigger>
          <DialogContent data-testid="scholarship-dialog" className="max-w-lg">
            <DialogHeader>
              <DialogTitle>{editMode ? 'Edit Scholarship' : 'Add New Scholarship'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-3">
              {/* Course & Session in one row */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="course" className="text-sm">Course *</Label>
                  <select
                    id="course"
                    className="w-full h-9 border border-gray-300 rounded-md px-2 text-sm"
                    value={formData.course_id}
                    onChange={(e) => {
                      const course = courses.find(c => c.id === e.target.value);
                      setFormData({ 
                        ...formData, 
                        course_id: e.target.value,
                        course_name: course?.name || '',
                        session_id: '',
                        session_name: ''
                      });
                    }}
                    required
                  >
                    <option value="">-- Select --</option>
                    {courses.map(course => (
                      <option key={course.id} value={course.id}>{course.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label htmlFor="session" className="text-sm">Session *</Label>
                  <select
                    id="session"
                    className="w-full h-9 border border-gray-300 rounded-md px-2 text-sm"
                    value={formData.session_id}
                    onChange={(e) => {
                      const session = sessions.find(s => s.id === e.target.value);
                      setFormData({ 
                        ...formData, 
                        session_id: e.target.value,
                        session_name: session?.name || ''
                      });
                    }}
                    required
                    disabled={!formData.course_id}
                  >
                    <option value="">-- Select --</option>
                    {sessions.map(session => (
                      <option key={session.id} value={session.id}>{session.name}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="name" className="text-sm">Name *</Label>
                  <Input
                    id="name"
                    className="h-9"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="e.g., Merit Award"
                    required
                    data-testid="scholarship-name-input"
                  />
                </div>
                <div>
                  <Label htmlFor="amount" className="text-sm">Amount (₹) *</Label>
                  <Input
                    id="amount"
                    className="h-9"
                    type="number"
                    min="0"
                    value={formData.amount}
                    onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                    placeholder="0.00"
                    required
                    data-testid="scholarship-amount-input"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="criteria" className="text-sm">Eligibility Criteria *</Label>
                <textarea
                  id="criteria"
                  className="w-full border border-gray-300 rounded-md p-2 text-sm min-h-[60px]"
                  value={formData.criteria}
                  onChange={(e) => setFormData({ ...formData, criteria: e.target.value })}
                  placeholder="e.g., GPA above 3.8, Top 5% of class"
                  required
                  data-testid="scholarship-criteria-input"
                />
              </div>

              <div>
                <Label className="text-sm">Entry Type *</Label>
                <div className="grid grid-cols-2 gap-2 mt-1">
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, entry_type: 'Regular', is_lateral_entry: false })}
                    className={`p-2 border-2 rounded-lg text-center text-sm transition-colors ${
                      formData.entry_type === 'Regular'
                        ? 'border-blue-500 bg-blue-50 text-blue-700'
                        : 'border-gray-300 hover:border-blue-300'
                    }`}
                  >
                    Regular
                  </button>
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, entry_type: 'Lateral', is_lateral_entry: true })}
                    className={`p-2 border-2 rounded-lg text-center text-sm transition-colors ${
                      formData.entry_type === 'Lateral'
                        ? 'border-orange-500 bg-orange-50 text-orange-700'
                        : 'border-gray-300 hover:border-orange-300'
                    }`}
                  >
                    Lateral
                  </button>
                </div>
              </div>

              <Button type="submit" className="w-full h-9" data-testid="submit-scholarship-button">
                {editMode ? 'Update Scholarship' : 'Create Scholarship'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Search & Filter */}
      <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-200">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-3 text-gray-400" size={20} />
            <Input
              placeholder="Search scholarships by name or criteria..."
              className="pl-10"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              data-testid="search-scholarships-input"
            />
          </div>
          <div>
            <select
              className="w-full h-10 border border-gray-300 rounded-md px-3"
              value={selectedCourseFilter}
              onChange={(e) => setSelectedCourseFilter(e.target.value)}
            >
              <option value="">All Courses</option>
              {courses.map(course => (
                <option key={course.id} value={course.id}>{course.name}</option>
              ))}
            </select>
          </div>
          <div>
            <select
              className="w-full h-10 border border-gray-300 rounded-md px-3"
              value={entryTypeFilter}
              onChange={(e) => setEntryTypeFilter(e.target.value)}
            >
              <option value="">All Entry Types</option>
              <option value="Regular">Regular Entry</option>
              <option value="Lateral">Lateral Entry</option>
            </select>
          </div>
        </div>
      </div>

      {/* Scholarships grouped by Course */}
      {Object.keys(groupedByCourse).length === 0 ? (
        <div className="bg-white p-12 rounded-xl shadow-sm border border-gray-200 text-center">
          <Award size={64} className="mx-auto text-gray-300 mb-4" />
          <p className="text-gray-500 mb-2">No scholarships found.</p>
          <p className="text-sm text-gray-400">Create your first scholarship program to support students.</p>
        </div>
      ) : (
        <div className="space-y-6">
          {Object.entries(groupedByCourse).map(([courseName, courseScholarships]) => (
            <div key={courseName} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="bg-gradient-to-r from-yellow-50 to-orange-50 px-6 py-4 border-b border-gray-200">
                <div className="flex items-center gap-3">
                  <div className="bg-yellow-600 p-2 rounded-lg">
                    <BookOpen size={24} className="text-white" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-gray-800">{courseName}</h2>
                    <p className="text-sm text-gray-600 mt-1">
                      {courseScholarships.length} scholarship(s) • Total: ₹{courseScholarships.reduce((sum, s) => sum + s.amount, 0).toLocaleString('en-IN')}
                    </p>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 p-6">
                {courseScholarships.sort((a, b) => b.amount - a.amount).map((scholarship, index) => (
                  <div 
                    key={scholarship.id} 
                    className="relative group border-2 border-gray-200 rounded-xl p-5 hover:border-yellow-400 hover:shadow-lg transition-all"
                    data-testid={`scholarship-card-${scholarship.id}`}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="bg-gradient-to-br from-yellow-100 to-orange-100 p-3 rounded-lg">
                        <Award size={28} className="text-yellow-600" />
                      </div>
                      <div className="flex flex-col gap-1">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                          scholarship.is_active 
                            ? 'bg-green-100 text-green-700' 
                            : 'bg-gray-100 text-gray-700'
                        }`}>
                          {scholarship.is_active ? 'Active' : 'Inactive'}
                        </span>
                        {scholarship.session_name && (
                          <span className="px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                            {scholarship.session_name}
                          </span>
                        )}
                      </div>
                    </div>

                    <h3 className="font-bold text-gray-800 text-lg mb-2">{scholarship.name}</h3>
                    
                    <div className="bg-gradient-to-br from-yellow-50 to-orange-50 rounded-lg p-3 mb-3">
                      <p className="text-3xl font-bold text-yellow-600">
                        ₹{scholarship.amount.toLocaleString('en-IN')}
                      </p>
                      <p className="text-xs text-gray-600 mt-1">Award Amount</p>
                    </div>

                    <div className="bg-gray-50 rounded-lg p-3 mb-3">
                      <p className="text-xs text-gray-500 mb-1">Criteria:</p>
                      <p className="text-sm text-gray-700 font-medium">{scholarship.criteria}</p>
                    </div>

                    <div className="flex items-center gap-2 text-xs">
                      <span className={`px-2 py-1 rounded ${
                        scholarship.entry_type === 'Lateral' 
                          ? 'bg-orange-100 text-orange-700' 
                          : 'bg-blue-100 text-blue-700'
                      }`}>
                        {scholarship.entry_type || 'Regular'}
                      </span>
                    </div>

                    {/* Action Buttons */}
                    <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity flex gap-1">
                      <button 
                        onClick={() => handleEdit(scholarship)}
                        className="p-2 bg-blue-100 hover:bg-blue-200 text-blue-600 rounded-lg transition-colors"
                        title="Edit"
                        data-testid={`edit-scholarship-${scholarship.id}`}
                      >
                        <Edit size={16} />
                      </button>
                      <button 
                        onClick={() => handleDelete(scholarship.id)}
                        className="p-2 bg-red-100 hover:bg-red-200 text-red-600 rounded-lg transition-colors"
                        title="Delete"
                        data-testid={`delete-scholarship-${scholarship.id}`}
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-gradient-to-br from-yellow-50 to-orange-50 p-6 rounded-xl border border-yellow-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-yellow-700 text-sm font-medium">Total Scholarships</p>
              <p className="text-3xl font-bold text-yellow-600 mt-2" data-testid="total-scholarships-stat">{totalScholarships}</p>
            </div>
            <Award className="text-yellow-400" size={40} />
          </div>
        </div>
        
        <div className="bg-gradient-to-br from-green-50 to-emerald-50 p-6 rounded-xl border border-green-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-green-700 text-sm font-medium">Total Awards</p>
              <p className="text-3xl font-bold text-green-600 mt-2">₹{totalAmount.toLocaleString('en-IN')}</p>
            </div>
            <TrendingUp className="text-green-400" size={40} />
          </div>
        </div>
        
        <div className="bg-gradient-to-br from-purple-50 to-pink-50 p-6 rounded-xl border border-purple-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-purple-700 text-sm font-medium">Average Award</p>
              <p className="text-3xl font-bold text-purple-600 mt-2">₹{Math.round(avgAmount).toLocaleString('en-IN')}</p>
            </div>
            <Star className="text-purple-400" size={40} />
          </div>
        </div>
        
        <div className="bg-gradient-to-br from-blue-50 to-indigo-50 p-6 rounded-xl border border-blue-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-blue-700 text-sm font-medium">Active Programs</p>
              <p className="text-3xl font-bold text-blue-600 mt-2">{activeScholarships}</p>
            </div>
            <Users className="text-blue-400" size={40} />
          </div>
        </div>
      </div>

      {/* Quick Insights */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-gradient-to-br from-yellow-50 to-orange-50 p-6 rounded-xl border border-yellow-200">
          <h3 className="font-semibold text-gray-800 mb-2 flex items-center gap-2">
            <Award size={20} className="text-yellow-600" />
            Highest Award
          </h3>
          <p className="text-2xl font-bold text-yellow-600 mb-1">
            ${maxScholarship.toLocaleString('en-IN')}
          </p>
          <p className="text-sm text-gray-600">
            Most prestigious scholarship available
          </p>
        </div>
        
        <div className="bg-gradient-to-br from-green-50 to-emerald-50 p-6 rounded-xl border border-green-200">
          <h3 className="font-semibold text-gray-800 mb-2 flex items-center gap-2">
            <TrendingUp size={20} className="text-green-600" />
            Budget Impact
          </h3>
          <p className="text-2xl font-bold text-green-600 mb-1">
            ₹{totalAmount.toLocaleString('en-IN')}
          </p>
          <p className="text-sm text-gray-600">
            Total scholarship budget allocation
          </p>
        </div>
        
        <div className="bg-gradient-to-br from-purple-50 to-pink-50 p-6 rounded-xl border border-purple-200">
          <h3 className="font-semibold text-gray-800 mb-2 flex items-center gap-2">
            <Star size={20} className="text-purple-600" />
            Award Range
          </h3>
          <p className="text-2xl font-bold text-purple-600 mb-1">
            ${minScholarship.toLocaleString('en-IN')} - ${maxScholarship.toLocaleString('en-IN')}
          </p>
          <p className="text-sm text-gray-600">
            Scholarship amount variation
          </p>
        </div>
      </div>

      {/* Info Banner */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-6">
        <div className="flex items-start gap-4">
          <div className="bg-blue-100 p-3 rounded-lg">
            <Award size={32} className="text-blue-600" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-bold text-gray-800 mb-2">Scholarship Program Guidelines</h3>
            <ul className="space-y-2 text-sm text-gray-600">
              <li className="flex items-start gap-2">
                <span className="text-blue-600 font-bold">•</span>
                <span>Define clear eligibility criteria for each scholarship program</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-blue-600 font-bold">•</span>
                <span>Review and update scholarship amounts annually based on budget</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-blue-600 font-bold">•</span>
                <span>Track scholarship recipients and monitor academic performance</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-blue-600 font-bold">•</span>
                <span>Ensure fair and transparent selection process for all applicants</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Scholarships;
